import PropTypes from "prop-types";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import EngagementBadge from "components/EngagementBadge";
import { GLASS_CARD, CHART_COLORS } from "utils/designTokens";
import { getCategoryColor } from "pages/digital-presence/data/social-media/categoryColorMapping";

// Stable colour from company ID hash
const companyColor = (id) => {
  if (!id) return CHART_COLORS[0];
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = ((h << 5) - h) + id.charCodeAt(i);
    h |= 0;
  }
  return CHART_COLORS[Math.abs(h) % CHART_COLORS.length];
};

// Format ISO date to "March 19, 2026"
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr + "T12:00:00");
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
};

// Format engagement count — 1234 → "1.2k"
const fmtEng = (n) => {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

/**
 * PostFeedCard
 *
 * Clickable card for a single LinkedIn post. The entire card opens the post URL.
 *
 * Layout:
 *   Row 1: date · company badge · category tag · engagement count · vsAvg badge
 *   Row 2: summary text (full, no truncation)
 *
 * Props:
 *   date        — ISO date string (string)
 *   companyName — display name for the company badge (string)
 *   companyId   — used to derive stable badge colour (string)
 *   category    — analysis.type value, e.g. "Clinical Evidence" (string)
 *   engagements — total engagement count (nLikes + nComments + nShares) (number)
 *   vsAvg       — vsAvg ratio (number | null)
 *   summary     — analysis.summary text (string)
 *   url         — post URL (string)
 */
function PostFeedCard({ date, dateLabel, companyName, companyId, category, engagements, vsAvg, summary, url }) {
  const badgeColor = companyColor(companyId);
  const catColor = category ? getCategoryColor(category) : "rgba(255,255,255,0.3)";
  const displayDate = dateLabel || formatDate(date);

  const handleClick = (e) => {
    if (e.defaultPrevented) return;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <VuiBox
      onClick={handleClick}
      sx={{
        background: GLASS_CARD.background,
        backdropFilter: GLASS_CARD.backdropFilter,
        borderRadius: "14px",
        border: "1px solid rgba(255,255,255,0.07)",
        p: "14px 16px",
        position: "relative",
        overflow: "hidden",
        cursor: url ? "pointer" : "default",
        transition: "border-color 0.18s ease, background 0.18s ease",
        "&:hover": {
          borderColor: "rgba(255,255,255,0.14)",
          background: "rgba(255,255,255,0.10)",
        },
        // Left accent bar in category colour
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0, left: 0, bottom: 0,
          width: "3px",
          borderRadius: "14px 0 0 14px",
          background: catColor,
          opacity: 0.65,
        },
      }}
    >
      {/* Row 1: identity left — content tags + engagement right */}
      <VuiBox display="flex" alignItems="center" gap={0.75} mb={0.875}>
        {/* Company badge — identity anchor, far left */}
        <VuiBox
          sx={{
            px: 0.875,
            py: 0.15,
            borderRadius: "5px",
            background: `${badgeColor}22`,
            border: `1px solid ${badgeColor}44`,
            flexShrink: 0,
          }}
        >
          <VuiTypography
            variant="caption"
            sx={{ fontSize: "0.68rem", color: badgeColor, fontWeight: "700", letterSpacing: "0.03em" }}
          >
            {companyName || "Company"}
          </VuiTypography>
        </VuiBox>

        {/* Spacer */}
        <VuiBox sx={{ flex: 1 }} />

        {/* Content tags + engagement — far right */}
        <VuiBox display="flex" alignItems="center" gap={0.75} flexShrink={0}>
          {/* Category tag */}
          {category && (
            <VuiBox
              sx={{
                px: 0.875,
                py: 0.15,
                borderRadius: "5px",
                background: `${catColor}18`,
                border: `1px solid ${catColor}33`,
                flexShrink: 0,
              }}
            >
              <VuiTypography
                variant="caption"
                sx={{ fontSize: "0.68rem", color: catColor, fontWeight: "600" }}
              >
                {category}
              </VuiTypography>
            </VuiBox>
          )}

          {/* Engagement count */}
          <VuiTypography
            variant="caption"
            sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", flexShrink: 0 }}
          >
            ★ {fmtEng(engagements)}
          </VuiTypography>

          {/* vsAvg badge */}
          <EngagementBadge vsAvg={vsAvg} />
        </VuiBox>
      </VuiBox>

      {/* Row 2: summary */}
      {summary && (
        <VuiTypography
          variant="caption"
          sx={{
            fontSize: "0.81rem",
            color: "rgba(255,255,255,0.72)",
            lineHeight: 1.6,
            display: "block",
            mb: 0.75,
          }}
        >
          {summary}
        </VuiTypography>
      )}

      {/* Row 3: date — after summary, consistent with PressArticleCard */}
      {displayDate && (
        <VuiTypography
          variant="caption"
          sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.28)", display: "block" }}
        >
          {displayDate}
        </VuiTypography>
      )}
    </VuiBox>
  );
}

PostFeedCard.propTypes = {
  date: PropTypes.string,
  dateLabel: PropTypes.string,
  companyName: PropTypes.string,
  companyId: PropTypes.string,
  category: PropTypes.string,
  engagements: PropTypes.number,
  vsAvg: PropTypes.number,
  summary: PropTypes.string,
  url: PropTypes.string,
};

PostFeedCard.defaultProps = {
  date: null,
  dateLabel: null,
  companyName: null,
  companyId: null,
  category: null,
  engagements: null,
  vsAvg: null,
  summary: null,
  url: null,
};

export default PostFeedCard;
